import React from 'react';
import { Loader2 } from 'lucide-react';
import { UploadProgress as UploadProgressType } from '../../types/upload.types';

interface Props {
  progress: UploadProgressType;
  onCancel?: () => void;
}

const ProgressBar: React.FC<{
  label: string;
  percent?: number;
  color: string;
  doneText?: string;
}> = ({ label, percent = 0, color, doneText }) => {
  const isDone = percent >= 100;
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-sm text-gray-300">
        <span>{label}</span>
        <span>{isDone ? (doneText || 'Done') : `${percent}%`}</span>
      </div>
      {!isDone && (
        <div className="w-full bg-[#161513] rounded-full h-2.5">
          <div
            className={`h-2.5 rounded-full transition-all duration-300 ease-out ${color}`}
            style={{ width: `${percent}%` }}
          />
        </div>
      )}
    </div>
  );
};

export const UploadProgress: React.FC<Props> = ({ progress, onCancel }) => {
  const { status, currentFile, uploadedFiles, totalFiles, uploadPercent = 0, blurPercent = 0, blurDone } = progress;

  if (status === 'idle' || status === 'completed' || status === 'error' || status === 'cancelled') return null;

  const uploadStatusText = () => {
    if (status === 'getting-credentials') return 'Preparing upload…';
    if (status === 'uploading' && uploadPercent < 100) return `Uploading ${uploadedFiles + 1} of ${totalFiles} images`;
    if (uploadPercent >= 100 && !blurDone) return 'Waiting for blur detection to complete…';
    if (uploadPercent >= 100 && blurDone) return 'Finishing up…';
    return 'Processing…';
  };

  return (
    <div className="mb-4 space-y-4">
      {/* Header Row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Loader2 className="w-5 h-5 animate-spin text-[#0CB4E4]" />
          <div className="text-sm font-medium text-white">{uploadStatusText()}</div>
        </div>
        {(status === 'uploading' || status === 'getting-credentials') && onCancel && (
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-slate-200 bg-slate-700 border border-slate-600 rounded-lg hover:bg-slate-600 transition-colors"
          >
            Cancel upload
          </button>
        )}
      </div>

      {/* Blur Bar */}
      <ProgressBar
        label="Blur Detection"
        percent={blurPercent}
        color="bg-[#0C5484]"
        doneText="Completed"
      />

      {/* Upload Bar */}
      <ProgressBar
        label="Image Upload"
        percent={uploadPercent}
        color="bg-green-400"
        doneText={blurDone ? 'Finishing up…' : undefined}
      />

      {status === 'uploading' && uploadPercent < 100 && (
        <div className="text-xs text-gray-400">Uploaded {uploadedFiles} of {totalFiles}</div>
      )}
    </div>
  );
};
